<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useAccount } from 'dashboard/composables/useAccount';

const props = defineProps({
  isOpen: {
    type: Boolean,
    default: false,
  },
  conversationUuid: {
    type: String,
    default: '',
  },
  reloadKey: {
    type: Number,
    default: 0,
  },
  crmAccountId: {
    type: [String, Number],
    default: '',
  },
  crmEmail: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['toggle']);
const { currentAccount } = useAccount();

const normalizeUrl = url => {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  if (url.startsWith('//')) return `https:${url}`;

  return `https://${url}`;
};

const baseUrl = computed(
  () => window.chatwootConfig?.orderSidebarEmbedUrl || ''
);
const isConfigured = computed(() => !!baseUrl.value);
const cmsProjectId = computed(() => currentAccount.value?.site_id || '');
const hasProjectId = computed(() => !!cmsProjectId.value);
const hasConversationUuid = computed(() => !!props.conversationUuid);

// 检查用户标识是否有效：排除空字符串、0、'0'、null、undefined
const hasCrmAccountId = computed(() => {
  const id = props.crmAccountId;
  // 如果是空字符串、null、undefined，返回 false
  if (!id && id !== 0) return false;
  // 如果是字符串，去除空格后检查
  const trimmedId = typeof id === 'string' ? id.trim() : String(id);
  // 排除空字符串和 '0'
  return trimmedId !== '' && trimmedId !== '0';
});

const buildSrc = () => {
  if (!isConfigured.value) return '';
  if (!hasProjectId.value) return '';
  if (!hasCrmAccountId.value) return '';
  if (!hasConversationUuid.value) return '';

  const url = new URL(normalizeUrl(baseUrl.value));
  url.searchParams.set('cms_project_id', cmsProjectId.value);
  url.searchParams.set('account_id', props.crmAccountId.toString());
  url.searchParams.set('conversation_uuid', props.conversationUuid);
  const trimmedEmail = props.crmEmail.toString().trim();
  if (trimmedEmail) {
    url.searchParams.set('email', trimmedEmail);
  }
  return url.toString();
};

const iframeSrc = computed(() => {
  if (!props.isOpen) return '';
  return buildSrc();
});

const expandVisible = ref(false);
// 弹窗 src 不依赖 isOpen，避免 isOpen 变化时 teleport 内 iframe patch 出错
const expandSrc = ref('');
const sidebarIframe = ref(null);

const notifySidebarIframe = expanded => {
  sidebarIframe.value?.contentWindow?.postMessage(
    { type: 'scanOrderExpandState', expanded },
    '*'
  );
};

const openExpand = () => {
  const src = buildSrc();
  if (src) {
    const url = new URL(src);
    url.searchParams.set('expanded', '1');
    expandSrc.value = url.toString();
  } else {
    expandSrc.value = src;
  }
  expandVisible.value = true;
  notifySidebarIframe(true);
};

const closeExpand = () => {
  expandVisible.value = false;
  notifySidebarIframe(false);
};

const handleMessage = event => {
  // 可以加 origin 校验：if (event.origin !== 'https://your-domain.com') return

  const { type, success } = event.data ?? {};

  switch (type) {
    case 'scanOrderExpand':
      openExpand();
      break;
    case 'scanOrderClose':
      closeExpand();
      break;
    case 'scanOrderSuccess':
      closeExpand();
      if (success) {
        // 刷新你们的数据
        emit('refresh');
      }
      break;
  }
};

onMounted(() => {
  window.addEventListener('message', handleMessage);
});

onUnmounted(() => {
  window.removeEventListener('message', handleMessage);
});
</script>

<template>
  <div>
    <button
      type="button"
      class="flex items-center justify-between w-full rounded-lg bg-n-slate-2 outline outline-1 outline-n-weak m-0 cursor-pointer py-2 px-4"
      @click.stop="emit('toggle')"
    >
      <div class="flex items-center gap-2 min-w-0">
        <span class="i-lucide-clipboard text-n-slate-11 text-base" />
        <h5 class="text-n-slate-12 text-sm mb-0 py-0 pr-2 pl-0 truncate">
          {{ $t('CONVERSATION.SIDEBAR.ORDER_PANEL.TITLE') }}
        </h5>
      </div>
      <div class="flex justify-end w-3 text-n-blue-text">
        <fluent-icon v-if="isOpen" size="24" icon="subtract" type="solid" />
        <fluent-icon v-else size="24" icon="add" type="solid" />
      </div>
    </button>
    <div
      v-if="isOpen"
      class="bg-n-background outline outline-1 outline-n-weak -mt-[-1px] border-t-0 rounded-br-lg rounded-bl-lg p-2"
    >
      <p v-if="!isConfigured" class="text-sm text-n-slate-11 mb-0 px-2 py-1">
        {{ $t('CONVERSATION.SIDEBAR.ORDER_PANEL.NOT_CONFIGURED') }}
      </p>
      <p
        v-else-if="!hasProjectId"
        class="text-sm text-n-slate-11 mb-0 px-2 py-1"
      >
        {{ $t('CONVERSATION.SIDEBAR.ORDER_PANEL.NO_PROJECT') }}
      </p>
      <p
        v-else-if="!hasCrmAccountId"
        class="text-sm text-n-slate-11 mb-0 px-2 py-1"
      >
        {{ $t('CONVERSATION.SIDEBAR.ORDER_PANEL.NO_USER_REGISTERED') }}
      </p>
      <p
        v-else-if="!hasConversationUuid"
        class="text-sm text-n-slate-11 mb-0 px-2 py-1"
      >
        {{ $t('CONVERSATION.SIDEBAR.EMBED.NO_CONVERSATION') }}
      </p>
      <div
        v-else
        class="w-full h-[560px] max-h-[65vh] rounded-md overflow-hidden"
      >
        <iframe
          ref="sidebarIframe"
          :key="reloadKey"
          class="w-full h-full bg-n-background"
          :src="iframeSrc"
          :title="$t('CONVERSATION.SIDEBAR.ORDER_PANEL.TITLE')"
          allow="clipboard-write"
        />
      </div>
    </div>
    <!-- 展开后的大弹窗，移到最外层避免 teleport 嵌套在 v-if 内导致的 subTree 错误 -->
    <teleport to="body">
      <div
        v-if="expandVisible"
        class="scan-order-overlay"
        @click.self="closeExpand"
      >
        <div class="scan-order-dialog">
          <iframe
            :src="expandSrc"
            width="100%"
            height="100%"
            frameborder="0"
          />
        </div>
      </div>
    </teleport>
  </div>
</template>

<style>
.scan-order-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
}

.scan-order-dialog {
  width: 800px;
  height: 90vh;
  background: #fff;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
}
</style>
